import * as React from 'react';
import classNames from 'classnames';
import Card from '../../sharedComponents/core/Cards/Card';
import FancyInput from './FancyInput';
import FancyButton from './FancyButton';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { ReactComponent as Logo } from '../../assets/logo/ttc-manager-login.svg';

const useStyles = createUseStyles((theme: Theme) => ({
  loginDialogueRoot: {},
  root: {
    display: 'flex',
    width: 378,
    flexDirection: 'column',
    background: 'white'
  },
  logoContainer: {
    padding: [15, 0, 20]
  },
  logo: {
    height: 40
  },
  heading: {
    fontWeight: 800,
    color: theme.colors.primaryBlack,
    fontSize: 24
  },
  subheading: {
    color: theme.colors.textGrey,
    fontWeight: 300,
    fontSize: 15,
    marginTop: 0,
    marginBottom: theme.spacing(2)
  },
  link: {
    margin: '22px 0 19px 0',
    textAlign: 'center',
    color: theme.colors.textBlue,
    fontSize: theme.fontSizes.small
  },
  errMessage: {
    color: '#43454a',
    fontWeight: 200,
    fontSize: 15,
    textAlign: 'center',
    margin: 3
  },
  links: {
    marginTop: 8,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  linkText: {
    fontSize: 15,
    letterSpacing: '-0.38',
    lineHeight: '25px',
    color: theme.colors.textBlue,
    textDecoration: 'none'
  },
  helpIcon: {
    cursor: 'pointer',
    color: theme.colors.textBlue,
    fontWeight: 'bold',
    fontSize: 21,
    lineHeight: `25px`,
    width: 32,
    height: 32,
    border: `1px solid rgba(16,129,170,0.17)`,
    marginRight: 14.5,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 50
  },
  helpTooltipContainer: {
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    filter: 'drop-shadow(5px 5px 2px rgba(0,0,0,0.15))'
  },
  helpTooltip: {
    position: 'relative',
    right: 50,
    width: '275px',
    borderRadius: '5px',
    padding: '15px',
    backgroundColor: theme.colors.primaryWhite,
    fontSize: theme.fontSizes.small,
    fontWeight: '400',
    color: theme.colors.textGrey,
    lineHeight: '15px',
    textAlign: 'center'
  },
  triangle: {
    zIndex: 1,
    position: 'relative',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderTop: `20px solid ${theme.colors.primaryWhite}`,
    marginBottom: '130px'
  },
  smallLink: {
    color: theme.colors.textBlue
  },
  white: {
    color: theme.colors.primaryWhite
  },
  whiteBorder: {
    color: theme.colors.primaryWhite,
    border: `1px solid rgba(255,255,255,0.4)`
  }
}));

type Props = {
  isManager?: boolean;
  onSubmit: (
    email: string,
    password: string,
    errorCallback: (err: string) => void
  ) => void;
  userIDLabel?: string;
};

function LoginDialogue({
  onSubmit,
  isManager,
  userIDLabel = 'Manager Email'
}: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [hover, setHover] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const onLogin = () => {
    onSubmit(email, password, (err) => {
      setError(err);
    });
  };
  return (
    <div className={classes.loginDialogueRoot}>
      <Card padding="medium" className={classes.root}>
        <div className={classes.logoContainer}>
          <Logo className={classes.logo} />
        </div>
        <h1 className={classes.heading}>Login to TTC Manager</h1>
        <p className={classes.subheading}>
          Glad to have you back, please enter your login details to proceed
        </p>
        <p className={classes.errMessage}>{error}</p>
        <form
          onSubmit={(evt) => {
            evt.preventDefault();
            onLogin();
          }}
        >
          <FancyInput
            label={userIDLabel}
            labelColor={'#737988'}
            type={'text'}
            onChange={setEmail}
          />
          <FancyInput
            label="Password"
            labelColor={'#737988'}
            type={'password'}
            onChange={setPassword}
          />
          <FancyButton text="Login to TTC" />
        </form>
        <a
          className={classes.link}
          href={
            isManager
              ? `//training.${process.env.REACT_APP_API_DOMAIN}`
              : `//manager.${process.env.REACT_APP_API_DOMAIN}`
          }
        >
          {`${isManager ? 'Training Login' : 'Manager Login'}`}
        </a>
      </Card>
      <div className={classes.links}>
        <a
          href={'mailto:admin@ttc-hub.com'}
          className={classNames(classes.linkText)}
        >
          <span
            className={classNames(classes.helpIcon)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            ?
            {hover && (
              <div className={classes.helpTooltipContainer}>
                <div className={classes.helpTooltip}>
                  <strong>Struggling to login?</strong> Don't worry, our
                  friendly team are here to help you recover your login details.
                  Feel free to contact{' '}
                  <span className={classes.smallLink}>admin@ttc-hub.com</span>{' '}
                  for help
                </div>
                <div className={classes.triangle} />
              </div>
            )}
          </span>
          Need Help?
        </a>
        <a href="/password" className={classNames(classes.linkText)}>
          Forgot password
        </a>
      </div>
    </div>
  );
}

export default LoginDialogue;
